import React, { ReactNode } from "react";
import { Control, Controller, FieldErrors, FieldValues, RegisterOptions, useFormContext } from "react-hook-form";
import { Box, IconButton, TextField } from "@mui/material";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import Visibility from "@mui/icons-material/Visibility";
import { handleHelperText } from "../shared/utils/helper-text-handler";
import InputLabel from "../layoutComponents/InputLabel";
import StrengthIndicator from "./StrengthIndicator/StrengthIndicator";
import { AutocompleteStyles } from "../shared/utils/autocomplete-styles";

interface PasswordInputProps {
	label: string | ReactNode;
	name: string;
	control: Control;
	defaultValue: string;
	errors: FieldErrors;
	watch: (names?: string | string[] | ((data: any, options: any) => void)) => string;
	validator: (
		name: string,
		watchedPsw?: any
	) => Omit<RegisterOptions<FieldValues, string>, "disabled" | "valueAsNumber" | "valueAsDate" | "setValueAs">;
	tabValue: number;
	gutterBottom?: boolean;
	showPasswordIndicator?: boolean;
}

const PasswordInput = ({
	name,
	control,
	label,
	watch,
	defaultValue,
	errors,
	validator,
	tabValue,
	gutterBottom = true,
	showPasswordIndicator,
}: PasswordInputProps) => {
	const [showPassword, setShowPassword] = React.useState(true);
	const watchedPassword = watch("password");

	const classes = AutocompleteStyles();
	const { clearErrors } = useFormContext();

	return (
		<Box marginBottom={gutterBottom ? 2 : 0}>
			{label && <InputLabel> {label}</InputLabel>}
			{showPasswordIndicator && <StrengthIndicator clearErrors={clearErrors} password={watchedPassword}></StrengthIndicator>}
			<Controller
				name={name}
				control={control}
				defaultValue={defaultValue}
				rules={validator(name, watchedPassword)}
				render={({ field: { onChange, value } }) => (
					<TextField
						fullWidth
						error={!!errors[name]}
						helperText={handleHelperText(errors, name)}
						onChange={onChange}
						type={showPassword ? "password" : "text"}
						value={value}
						inputProps={{ tabIndex: tabValue }}
						InputProps={{
							classes: { root: classes.inputRoot },
							endAdornment: (
								<IconButton
									aria-label="toggle password visibility"
									edge="end"
									onClick={() => setShowPassword(!showPassword)}
									tabIndex={-1}
								>
									{showPassword ? <Visibility /> : <VisibilityOff />}
								</IconButton>
							),
						}}
					/>
				)}
			/>
		</Box>
	);
};

export default PasswordInput;
